import { api } from "./index";

export type CertificateActionLogType =
  | 'create_certificate'
  | 'admin_transfer'
  | 'admin_cancel_transfer'
  | 'confirm_transfer'
  | 'confirm_request_cancel_transfer'
  | 'accept_transfer'
  | 'CertificateStatusChange'
  | 'certificate_archived'
  | 'certificate_unarchived'
  | string;

export interface CertificateActivityActor {
  id: string;
  // The action-log API returns first_name / last_name (and role as a plain name
  // string), NOT a composed full_name / role object — mapItemToRow normalises them.
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  avatar: string | null;
  email?: string | null;
  role?: { id: string; name: string } | string | null;
}

// Snapshotted at event time. Transfer events carry the recipient id + a
// price/currency snapshot; the status objects record the before→after transition.
export interface CertificateActivityMetadata {
  end_user_id?: string | null;
  price?: number | string | null;
  currency?: string | null;
  transfer?: { id?: string; status?: { from?: string | null; to?: string | null } } | null;
  certificate_status?: { from?: string | null; to?: string | null } | null;
  [key: string]: unknown;
}

// VRG-231 review item 6 — the actual API response also carries the certificate's
// OWN snapshot at event time (and a top-level certificate_status), which the type
// below previously omitted. This is what lets the label builder tell a group
// PARENT's "add_to_group" event (group_divisibility set, no parent_certificate_id)
// apart from a CHILD's ("Created a certificate group" vs "Added <product> to group").
export interface CertificateActivitySnapshot {
  id: string;
  parent_certificate_id?: string | null;
  group_divisibility?: 'divisible' | 'indivisible' | null;
  [key: string]: unknown;
}

export interface CertificateActivityItem {
  id: string;
  type: CertificateActionLogType;
  metadata: CertificateActivityMetadata | null;
  created_at: string;
  user_type: 'anonymous' | 'customer' | 'admin' | 'superadmin';
  user: CertificateActivityActor | null;
  // The transfer RECIPIENT, resolved server-side from metadata.end_user_id
  // (action-log.aggregate.ts → user_to). Present on transfer events.
  user_to?: CertificateActivityActor | null;
  certificate?: CertificateActivitySnapshot | null;
  certificate_status?: string | null;
}

export interface CertificateActivityResponse {
  data: CertificateActivityItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CertificateActivityParams {
  limit?: number;
  offset?: number;
  sort?: string;
}

export const certificateActivityApi = {
  async getCertificateActivity(
    certificateId: string,
    params?: CertificateActivityParams
  ): Promise<CertificateActivityResponse> {
    const response = await api.get(`/action-log/${certificateId}`, { params });
    return response.data;
  },
};
